import React from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { useCases } from '../../contexts/api-contexts/CasesContext';

interface CaseSymptomsSummaryProps {
  isCollapsed?: boolean;
  isActive?: boolean;
}

export const CaseSymptomsSummary: React.FC<CaseSymptomsSummaryProps> = ({
  isCollapsed = false,
  isActive = false,
}) => {
  const { currentCase } = useCases();
  const backgroundColor = isActive ? 'rgba(0, 150, 136, 0.12)' : 'transparent';

  const getSummaryText = (): { text: string; count: string | null } => {
    // Check if we have a patient
    if (!currentCase?.patient) {
      return {
        text: 'Please complete case information to get started',
        count: null,
      };
    }

    // Count selected symptoms
    const selectedSymptomsCount = currentCase?.selected_symptoms?.length || 0;

    // Count potential symptoms (suggested + sanitized)
    const suggestedSymptomsCount = currentCase?.suggested_symptoms?.length || 0;
    const potentialSymptomsCount = suggestedSymptomsCount;

    if (selectedSymptomsCount === 0) {
      return {
        text: `potential symptoms available`,
        count: `${potentialSymptomsCount}`,
      };
    }

    return {
      text: `out of ${potentialSymptomsCount} potential symptoms chosen`,
      count: `${selectedSymptomsCount}`,
    };
  };

  // Collapsed view - just show icon
  if (isCollapsed) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 1.5,
          backgroundColor,
          borderLeft: isActive ? '3px solid #009688' : 'none',
        }}
      >
        <IconButton size="small" color={isActive ? 'primary' : 'default'}>
          <HealthAndSafetyIcon />
        </IconButton>
      </Box>
    );
  }

  // Expanded view
  return (
    <Box
      sx={{
        p: 2,
        backgroundColor,
        borderLeft: isActive ? '3px solid #009688' : 'none',
      }}
    >
      <Box display="flex" alignItems="center" mb={1}>
        <HealthAndSafetyIcon
          color={isActive ? 'primary' : 'inherit'}
          sx={{ mr: 1 }}
        />
        <Typography
          variant="subtitle1"
          color={isActive ? 'primary' : 'inherit'}
        >
          Symptoms
        </Typography>
      </Box>
      <Typography
        variant="body2"
        color="text.secondary"
        component="div"
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        {getSummaryText().count && (
          <Chip
            label={getSummaryText().count}
            sx={{
              bgcolor: '#00c853',
              color: 'white',
              fontWeight: 'bold',
              mr: 1,
              '&:hover': {
                bgcolor: '#00a045',
                cursor: 'pointer',
              },
            }}
            size="small"
          />
        )}
        {getSummaryText().text}
      </Typography>
    </Box>
  );
};

export default CaseSymptomsSummary;
